<template>
  <div class="flex flex-col bg-[#f8fafc] gap-4">
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] flex flex-col pt-3">
      <div class="relative z-30 px-6 pb-3 flex items-center justify-between gap-4 border-b border-slate-100 bg-white">
        <h2 class="text-base font-bold text-[#0d4169] text-left m-0">HR</h2>
        <button
          type="button"
          :disabled="!canAddWorkShiftTime"
          class="px-4 py-2 bg-[#0d4169] text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-[#0d4169]/10 hover:bg-[#0a3354] flex items-center gap-2 shrink-0"
          @click="openModal()"
        >
          + Add Shift
        </button>
      </div>

      <div class="relative z-0 px-6 py-4">
        <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
          <div class="px-6 pt-2 pb-1">
            <div class="flex items-baseline gap-2 flex-wrap">
              <h2 class="text-base font-bold text-[#0d4169] text-left">
                Work / Shift Time
              </h2>
            </div>
          </div>

          <div class="px-6 pb-6 pt-2">
            <p v-if="errorMessage" class="mb-3 text-sm text-red-600">{{ errorMessage }}</p>
            <div class="w-full overflow-x-auto">
              <table class="w-full min-w-[980px] border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th @click="sortBy('shiftName')" class="sort-col">
                      Working Shift
                      <span v-if="sortKey === 'shiftName'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('earlyCheckIn')" class="sort-col">
                      Early Check In
                      <span v-if="sortKey === 'earlyCheckIn'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('earlyCheckOut')" class="sort-col">
                      Early Check Out
                      <span v-if="sortKey === 'earlyCheckOut'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('lateCheckIn')" class="sort-col">
                      Late Check In
                      <span v-if="sortKey === 'lateCheckIn'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('lateCheckOut')" class="sort-col">
                      Late Check Out
                      <span v-if="sortKey === 'lateCheckOut'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('officeHour')" class="sort-col">
                      Office Hour
                      <span v-if="sortKey === 'officeHour'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="8" class="text-center py-10">
                      <div class="flex items-center justify-center">
                        <div class="h-8 w-8 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-for="(row, idx) in sortedRows"
                    v-else
                    :key="row.id"
                    class="hover:bg-slate-100/50 transition-colors border-b"
                  >
                    <td>{{ idx + 1 }}</td>
                    <td>{{ row.shiftName }}</td>
                    <td>{{ row.earlyCheckIn }}</td>
                    <td>{{ row.earlyCheckOut }}</td>
                    <td>{{ row.lateCheckIn }}</td>
                    <td>{{ row.lateCheckOut }}</td>
                    <td>{{ row.officeHour }}</td>
                    <td class="actions">
                      <button type="button" :disabled="!canEditWorkShiftTime" @click="openModal(row)">
                        Edit
                      </button>
                      <label class="toggle-switch" title="Active">
                        <input
                          type="checkbox"
                          :checked="row.active"
                          :disabled="togglingId === row.id"
                          @change="toggleActive(row)"
                        >
                        <span class="slider"></span>
                      </label>
                    </td>
                  </tr>
                  <tr v-if="!loading && rows.length === 0">
                    <td colspan="8" style="text-align:center; color:#94a3b8; padding: 32px 15px; font-style: italic;">
                      No work / shift times saved.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <WorkOrShiftTimeModal
    :is-open="isModalOpen"
    :edit-shift="editingShift"
    @close="closeModal"
    @save="saveShift"
  />
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { apiFetch } from '../../../utils/api'
import WorkOrShiftTimeModal from './workOrShiftTimeModal.vue'

const API_BASE = '/hr/work-or-shift-time'
const PRIVILEGE_NAME = 'HR Work/Shift Time'

const store = useStore()
const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canAddWorkShiftTime = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(PRIVILEGE_NAME), 'Add')
})

const canEditWorkShiftTime = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(PRIVILEGE_NAME), 'Edit')
})

const rows = ref([])
const loading = ref(false)
const errorMessage = ref('')
const isModalOpen = ref(false)
const editingShift = ref(null)
const togglingId = ref(null)
const sortKey = ref('shiftName')
const sortOrder = ref('asc')

const numericSortKeys = new Set(['earlyCheckIn', 'earlyCheckOut', 'lateCheckIn', 'lateCheckOut'])

const sortedRows = computed(() => {
  const result = [...rows.value]

  result.sort((a, b) => {
    if (!!a.active !== !!b.active) {
      return a.active ? -1 : 1
    }

    const modifier = sortOrder.value === 'asc' ? 1 : -1
    const key = sortKey.value

    if (numericSortKeys.has(key)) {
      const valA = Number(a[key] ?? 0)
      const valB = Number(b[key] ?? 0)
      if (valA < valB) return -1 * modifier
      if (valA > valB) return 1 * modifier
      return 0
    }

    const valA = (a[key] ?? '').toString().toLowerCase()
    const valB = (b[key] ?? '').toString().toLowerCase()
    if (valA < valB) return -1 * modifier
    if (valA > valB) return 1 * modifier
    return 0
  })

  return result
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function openModal(row = null) {
  editingShift.value = row ? { ...row } : null
  isModalOpen.value = true
}

function closeModal() {
  isModalOpen.value = false
  editingShift.value = null
}

async function fetchRows() {
  loading.value = true
  errorMessage.value = ''
  try {
    const res = await apiFetch(API_BASE)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to load work / shift times')
    }
    const data = await res.json().catch(() => [])
    rows.value = Array.isArray(data) ? data : []
  } catch (err) {
    console.error('Error loading work/shift times:', err)
    rows.value = []
    errorMessage.value = err.message || 'Failed to load work / shift times'
  } finally {
    loading.value = false
  }
}

async function saveShift(payload, handlers = {}) {
  const { resolve, reject } = handlers
  errorMessage.value = ''
  try {
    const isEdit = !!editingShift.value?.id
    if (isEdit ? !canEditWorkShiftTime.value : !canAddWorkShiftTime.value) {
      reject?.(new Error('You do not have permission to save this shift'))
      return
    }
    const res = await apiFetch(
      isEdit ? `${API_BASE}/${editingShift.value.id}` : API_BASE,
      {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    )

    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || (isEdit ? 'Failed to update shift' : 'Failed to save shift'))
    }

    closeModal()
    await fetchRows()
    resolve?.()
  } catch (err) {
    console.error('Error saving work/shift time:', err)
    reject?.(err)
  }
}

async function toggleActive(row) {
  if (!row?.id || togglingId.value) return
  togglingId.value = row.id
  errorMessage.value = ''
  try {
    const res = await apiFetch(`${API_BASE}/${row.id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !row.active }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || 'Failed to update status')
    }
    const idx = rows.value.findIndex((r) => r.id === row.id)
    if (idx >= 0) {
      rows.value[idx] = { ...rows.value[idx], ...data }
    } else {
      await fetchRows()
    }
  } catch (err) {
    console.error('Error toggling work/shift time status:', err)
    window.alert(err.message || 'Failed to update status')
  } finally {
    togglingId.value = null
  }
}

onMounted(() => {
  fetchRows()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #1e293b;
}

th,
td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  font-size: 0.7rem;
}

th {
  background: #f1f5f9;
  font-weight: 600;
  cursor: default;
}

th.sort-col {
  cursor: pointer;
}

th.sort-col:hover {
  background: #e2e8f0;
}

tbody tr:hover {
  background: #f1f5f9;
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  inset: 0;
  background: #ef4444;
  border-radius: 34px;
  transition: 0.2s;
}

.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  bottom: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}

.toggle-switch input:checked + .slider {
  background: #22c55e;
}

.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}
</style>
